import {
  TaskTableOwnerPills,
  TaskTableStatusPill,
  type TaskTableStatusPillTone,
} from '@hellodarwin/app-core/lib/components/common/task-table';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import { formatUtcDate } from '@hellodarwin/core/lib/features/helpers/get-formatted-date';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import Close from '@hellodarwin/icons/dist/icons/Close';
import Checkbox from 'antd/es/checkbox';
import Drawer from 'antd/es/drawer';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import { useTheme } from 'styled-components';
import type { ClientTask } from '../../features/api/client-task-types';
import { getTaskDaysRemaining } from './task-date';
import {
  getTaskDrawerStyles,
  TaskDrawerCloseButton,
  TaskDrawerContent,
  TaskDrawerFormContent,
  TaskDrawerFormStyles,
  TaskDrawerHeader,
  TaskDrawerMetaList,
  TaskDrawerReadOnlySelect,
} from './styles';

interface TaskDetailDrawerProps {
  task: ClientTask | undefined;
  selectedLocale: string;
  mutationPending: boolean;
  onClose: () => void;
  onSetCompleted: (taskId: string, completed: boolean) => Promise<unknown>;
}

const fullName = (firstName: string, lastName: string) =>
  [firstName, lastName].filter(Boolean).join(' ');

export const TaskDetailDrawer = ({
  task,
  selectedLocale,
  mutationPending,
  onClose,
  onSetCompleted,
}: TaskDetailDrawerProps) => {
  const { t } = useTranslations();
  const theme = useTheme();
  const unavailable = t('program-workspace|taskDetailUnavailable');

  if (!task) return null;

  const displayDate = (value: string | null) =>
    value ? formatUtcDate(value, selectedLocale) : unavailable;
  const assigneeNames = task.client_assignees
    .map((assignee) => fullName(assignee.first_name, assignee.last_name))
    .filter(Boolean);
  const daysRemaining = getTaskDaysRemaining(task.due_at);
  const duePill: { label: string; tone: TaskTableStatusPillTone } =
    daysRemaining === null
      ? {
          label: t('program-workspace|taskNoDueDate'),
          tone: 'neutral',
        }
      : daysRemaining < 0
        ? {
            label: t('program-workspace|taskOverdue'),
            tone: 'danger',
          }
        : {
            label: t('program-workspace|taskDaysRemaining', {
              count: daysRemaining,
            }),
            tone: daysRemaining <= 7 ? 'warning' : 'success',
          };

  return (
    <Drawer
      open
      closeIcon={false}
      destroyOnHidden
      maskClosable={false}
      onClose={onClose}
      styles={getTaskDrawerStyles(theme)}
      width={theme.caps.xxs - theme.spacing.sm}
      title={
        <TaskDrawerHeader>
          <TaskDrawerCloseButton
            aria-label={t('program-workspace|taskDetailClose')}
            onClick={onClose}
            type="button"
          >
            <Close size={16} />
          </TaskDrawerCloseButton>
          <Typography.Title level={4} elementTheme="h4" noMargin>
            {t('program-workspace|taskDetailTitle')}
          </Typography.Title>
        </TaskDrawerHeader>
      }
    >
      <TaskDrawerFormStyles>
        <Form layout="vertical">
          <TaskDrawerContent>
            <TaskDrawerFormContent>
              <Form.Item label={t('program-workspace|taskDetailTitleLabel')}>
                <Input readOnly value={task.title} />
              </Form.Item>
              <Form.Item label={t('program-workspace|taskDetailNotes')}>
                <Input.TextArea
                  readOnly
                  rows={4}
                  value={task.notes || unavailable}
                />
              </Form.Item>
              <Form.Item label={t('program-workspace|taskDetailAssignees')}>
                <TaskDrawerReadOnlySelect>
                  <TaskTableOwnerPills
                    names={assigneeNames}
                    fallbackLabel={t(
                      'program-workspace|taskAssignedToClientShort',
                    )}
                  />
                </TaskDrawerReadOnlySelect>
              </Form.Item>
              <Form.Item label={t('program-workspace|taskDetailDueDate')}>
                <Input readOnly value={displayDate(task.due_at)} />
              </Form.Item>
              <TaskDrawerMetaList>
                <Typography
                  elementTheme="body3"
                  style={{ color: theme.colors.grey_2 }}
                >
                  {t('program-workspace|taskDetailTimeRemaining')}
                </Typography>
                <TaskTableStatusPill $tone={duePill.tone}>
                  {duePill.label}
                </TaskTableStatusPill>
              </TaskDrawerMetaList>
              <Form.Item label={t('program-workspace|taskDetailStatus')}>
                <Checkbox
                  aria-label={t('program-workspace|taskDetailStatusToggle', {
                    title: task.title,
                  })}
                  checked={task.status === 'completed'}
                  disabled={mutationPending}
                  onChange={(event) => {
                    void onSetCompleted(
                      task.task_id,
                      event.target.checked,
                    ).catch(() => undefined);
                  }}
                >
                  <Typography elementTheme="body3">
                    {t('program-workspace|taskStatuscompleted')}
                  </Typography>
                </Checkbox>
              </Form.Item>
            </TaskDrawerFormContent>
          </TaskDrawerContent>
        </Form>
      </TaskDrawerFormStyles>
    </Drawer>
  );
};
